import { NgOptimizedImage, NgTemplateOutlet, UpperCasePipe } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, OnDestroy, computed, effect, signal, viewChild } from '@angular/core';
import { RouterLink } from '@angular/router';
import { PairLogoComponent } from '@components/pair-logo/pair-logo.component';
import { PopperIconComponent } from '@components/popper-icon/popper-icon.component';
import { TokenInfoListComponent } from '@components/token-info-list/token-info-list.component';
import { FormatPipe } from '@pipes/format.pipe';
import { RxFor } from '@rx-angular/template/for';
import { ModalService } from '@services/modal-service.service';
import { ScreenSizeService } from '@services/screen-size.service';
import { aggregatedData, chain, chainBasePair, chainTokenPrice, chainTopGainersThreshold } from '@stores/chain.store';
import { chainSdTokens } from '@stores/token.store';
import { SafeBig, compareAddress } from '@utils/index';
import KeenSlider, { KeenSliderInstance } from 'keen-slider';
import { hostBinding } from 'ngxtension/host-binding';
import { NgTemplateNameDirective } from '../../../shared/directives/ng-template-name.directive';
import { TokenTableComponent } from './token-table/token-table.component';

@Component({
  selector: 'cmp-home-page',
  standalone: true,
  imports: [
    TokenInfoListComponent,
    PairLogoComponent,
    FormatPipe,
    TokenTableComponent,
    PopperIconComponent,
    UpperCasePipe,
    NgOptimizedImage,
    RxFor,
    RouterLink,
    PopperIconComponent,
    NgTemplateNameDirective,
    NgTemplateOutlet,
  ],
  providers: [FormatPipe],
  templateUrl: './home-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styles: ``,
})
export class HomePageComponent implements AfterViewInit, OnDestroy {
  class = hostBinding('class', signal('home-page'));
  statsSliderRef = viewChild<ElementRef>('statsSlider');

  slider: KeenSliderInstance = null;

  aggregatedData = computed(() => {
    const data = aggregatedData();
    const basePrices = chainTokenPrice();

    if (!data || !basePrices) return [];

    const bscRaised = SafeBig(data?.bsc?.total?.raised).mul(basePrices['BNB']);
    const ethRaised = SafeBig(data?.eth?.total?.raised).mul(basePrices['ETH']);
    const baseRaised = SafeBig(data?.base?.total?.raised).mul(basePrices['ETH']);

    return [
      {
        title: 'SmartDeFi™ 24h volume',
        data: {
          total: data?.combined?.total?.dailyVolume,
          bsc: data?.bsc?.total?.dailyVolume,
          eth: data?.eth?.total?.dailyVolume,
          base: data?.base?.total?.dailyVolume,
        },
        formatParams: { abbreviate: true, decimals: 2, sign: '$' },
      },
      {
        title: 'SmartDeFi™ tokens launched',
        data: {
          total: data?.combined?.count?.tokens,
          bsc: data?.bsc?.count?.tokens,
          eth: data?.eth?.count?.tokens,
          base: data?.base?.count?.tokens,
        },
      },
      {
        title: 'Total raised in presales',
        data: {
          total: this.formatPipe.transform(bscRaised.add(ethRaised).add(baseRaised).toString(), { abbreviate: true, decimals: 2, sign: '$' }),
          bsc: `${this.formatPipe.transform(data?.bsc?.total?.raised, { decimals: 1, abbreviate: true })} BNB`,
          eth: `${this.formatPipe.transform(data?.eth?.total?.raised, { decimals: 1, abbreviate: true })} ETH`,
          base: `${this.formatPipe.transform(data?.base?.total?.raised, { decimals: 1, abbreviate: true })} ETH`,
        },
        skipPipeFormat: true,
        formatParams: null,
      },
      {
        title: 'SmartDeFi™ holders',
        data: {
          total: data?.combined?.count?.holders,
          bsc: data?.bsc?.count?.holders,
          eth: data?.eth?.count?.holders,
          base: data?.base?.count?.holders,
        },
        formatParams: { abbreviate: true, decimals: 2 },
      },
      {
        title: 'SmartDeFi™ LP pairs',
        data: {
          total: data?.combined?.count?.pairs,
          bsc: data?.bsc?.count?.pairs,
          eth: data?.eth?.count?.pairs,
          base: data?.base?.count?.pairs,
        },
      },
      {
        title: 'SmartDeFi™ token stakes created',
        data: {
          total: data?.combined?.count?.stakes,
          bsc: data?.bsc?.count?.stakes,
          eth: data?.eth?.count?.stakes,
          base: data?.base?.count?.stakes,
        },
      },
      {
        title: 'Total SmartDeFi™ presales',
        data: {
          total: data?.combined?.count?.presales,
          bsc: data?.bsc?.count?.presales,
          eth: data?.eth?.count?.presales,
          base: data?.base?.count?.presales,
        },
      },
      {
        title: 'SmartDeFi™ presales succeded',
        data: {
          total: data?.combined?.count?.succededPresales,
          bsc: data?.bsc?.count?.succededPresales,
          eth: data?.eth?.count?.succededPresales,
          base: data?.base?.count?.succededPresales,
        },
      },
      {
        title: 'SmartDeFi™ FEG pairs',
        data: {
          total: data?.combined?.count?.fegPairs,
          bsc: data?.bsc?.count?.fegPairs,
          eth: data?.eth?.count?.fegPairs,
          base: data?.base?.count?.fegPairs,
        },
      },
    ];
  });

  trendingTokens = computed(() =>
    chainSdTokens()
      .filter(
        ({ ethLp, priceChange, address }) =>
          SafeBig(ethLp?.eth).gt(chainTopGainersThreshold()) &&
          SafeBig(priceChange).gt(0) &&
          !compareAddress(address, '0x32F1dE611AC659Be08f8a84e94E5c0bd66D46efd'),
      )
      .sort((a, b) => SafeBig(b?.priceChange).sub(SafeBig(a?.priceChange)).toNumber())
      .slice(0, 5),
  );
  newListings = computed(() =>
    chainSdTokens()
      .filter((token) => !!Number(token.tokenPrice))
      .sort((a, b) => SafeBig(b.createdTimestamp).sub(SafeBig(a.createdTimestamp)).toNumber())
      .slice(0, 5),
  );

  totalChainVolume = computed(() =>
    chainSdTokens()
      .reduce(
        (
          acc,
          {
            data24h: {
              volume24hData: { total },
            },
          },
        ) => acc.add(total),
        SafeBig(0),
      )
      .toString(),
  );

  topGainersTooltip = computed(() => `Requires at least ${chainTopGainersThreshold()} ${chainBasePair()} in the main pairs LP.`);

  newListingsTooltip = computed(() => `Newest SD listings with added main pair liqudidity`);

  get chainTokens() {
    return chainSdTokens;
  }

  get chain() {
    return chain;
  }

  test($event) {
    console.log({ $event });
  }

  ngAfterViewInit(): void {
    this.slider = new KeenSlider(this.statsSliderRef().nativeElement, { slides: { perView: 'auto', spacing: 10 } });
  }

  constructor(
    private modalService: ModalService,
    private formatPipe: FormatPipe,
    public screenSizeService: ScreenSizeService,
  ) {
    effect(() => console.log({ chainTokenPrice: chainTokenPrice() }));

    effect(() => {
      this.aggregatedData();
      this.slider.update();
    });
  }

  openTokenPreview(address) {
    this.modalService.openTokenPreview(address);
  }

  ngOnDestroy(): void {
    this.slider.destroy();
  }
}
